import { useState, useEffect } from "react";
// Real-time number formatter with commas for any numeric value
export const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // Convert to string if it's a number
  const stringValue = value.toString();
  
  // Remove all non-digit characters except decimal point and minus sign
  const cleanValue = stringValue.replace(/[^\d.-]/g, '');
  
  // If empty after cleaning, return empty string
  if (cleanValue === '' || cleanValue === '-' || cleanValue === '.') return cleanValue;
  
  // Split into whole and decimal parts
  const parts = cleanValue.split('.');
  let wholePart = parts[0];
  let decimalPart = parts[1] || '';
  
  // Handle negative numbers
  let isNegative = false;
  if (wholePart.startsWith('-')) {
    isNegative = true;
    wholePart = wholePart.substring(1);
  }
  
  // Format whole part with commas
  if (wholePart) {
    // Remove leading zeros
    wholePart = wholePart.replace(/^0+/, '') || '0';
    wholePart = parseInt(wholePart, 10).toLocaleString('en-NG');
  }
  
  // Limit decimal places to 2 for currency, but allow more for other numbers
  if (decimalPart) {
    decimalPart = decimalPart.slice(0, 10); // Reasonable limit
  }
  
  // Combine whole and decimal parts
  let result = wholePart;
  if (decimalPart) {
    result = `${wholePart}.${decimalPart}`;
  }
  
  // Add back negative sign
  if (isNegative) {
    result = `-${result}`;
  }
  
  return result;
};

// Parse formatted number back to raw number for calculations
export const parseFormattedNumber = (formattedValue) => {
  if (!formattedValue && formattedValue !== 0) return 0;
  
  // Remove all commas and parse as float
  const cleanValue = formattedValue.toString().replace(/,/g, '');
  const parsedValue = parseFloat(cleanValue);
  
  return isNaN(parsedValue) ? 0 : parsedValue;
};

// Number formatter for display with currency symbol
export const formatAmount = (value) => {
  const num = Number(value) || 0;
  return `₦${num.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Number formatter for display without currency symbol
export const formatNumber = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Main hook for formatted number inputs - DYNAMIC VERSION
export const useFormattedInputs = (initialState = {}) => {
  const [formattedValues, setFormattedValues] = useState(initialState);
  const [numericValues, setNumericValues] = useState({});

  // Initialize numeric values
  useEffect(() => {
    const initialNumericValues = {};
    Object.keys(initialState).forEach(key => {
      initialNumericValues[key] = parseFormattedNumber(initialState[key]);
    });
    setNumericValues(initialNumericValues);
  }, []);

  const handleChange = (fieldName, value) => {
    const formattedValue = formatNumberWithCommas(value);
    
    setFormattedValues(prev => ({
      ...prev,
      [fieldName]: formattedValue
    }));
    
    setNumericValues(prev => ({
      ...prev,
      [fieldName]: parseFormattedNumber(formattedValue)
    }));
  };

  const setValue = (fieldName, value) => {
    const formattedValue = formatNumberWithCommas(value);
    
    setFormattedValues(prev => ({
      ...prev,
      [fieldName]: formattedValue
    }));
    
    setNumericValues(prev => ({
      ...prev,
      [fieldName]: parseFormattedNumber(formattedValue)
    }));
  };

  const reset = (fieldName = null) => {
    if (fieldName) {
      setFormattedValues(prev => ({ ...prev, [fieldName]: '' }));
      setNumericValues(prev => ({ ...prev, [fieldName]: 0 }));
    } else {
      setFormattedValues(initialState);
      const resetNumericValues = {};
      Object.keys(initialState).forEach(key => {
        resetNumericValues[key] = parseFormattedNumber(initialState[key]);
      });
      setNumericValues(resetNumericValues);
    }
  };

  const getNumericValue = (fieldName) => {
    return numericValues[fieldName] || 0;
  };

  const getFormattedValue = (fieldName) => {
    return formattedValues[fieldName] || '';
  };

  return {
    formattedValues,
    numericValues,
    handleChange,
    setValue,
    reset,
    getNumericValue,
    getFormattedValue
  };
};

// Higher-order component for forms with multiple number fields
export const withNumberFormatting = (WrappedComponent, numberFields = []) => {
  return function WithNumberFormatting(props) {
    const [formattedValues, setFormattedValues] = useState({});
    
    const handleFormattedChange = (fieldName, value) => {
      const formattedValue = formatNumberWithCommas(value);
      setFormattedValues(prev => ({
        ...prev,
        [fieldName]: formattedValue
      }));

      // Pass both formatted and numeric values to parent
      if (props.onFieldChange) {
        const numericValue = parseFormattedNumber(formattedValue);
        props.onFieldChange(fieldName, formattedValue, numericValue);
      }
    };

    return (
      <WrappedComponent
        {...props}
        formattedValues={formattedValues}
        onFormattedChange={handleFormattedChange}
      />
    );
  };
};

// Utility function to create formatted input handler
export const createFormattedHandler = (setStateFunction) => {
  return (fieldName, value) => {
    const formattedValue = formatNumberWithCommas(value);
    const numericValue = parseFormattedNumber(formattedValue);
    
    setStateFunction(prev => ({
      ...prev,
      [fieldName]: formattedValue,
      [`${fieldName}Numeric`]: numericValue
    }));
  };
};

// Direct integration for existing forms
export const integrateNumberFormatting = (formData, setFormData) => {
  const handleFormattedInput = (fieldName, value) => {
    const formattedValue = formatNumberWithCommas(value);
    const numericValue = parseFormattedNumber(formattedValue);
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: formattedValue,
      [`${fieldName}Numeric`]: numericValue
    }));
  };

  return handleFormattedInput;
};